import React from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import { ceraProBold, openSans, vars } from '../../../Theme';
import i18next from 'i18next';

const dialogSx = {
    '& .MuiDialog-paper': {
        width: '90%',
        minWidth: '350px',
        maxWidth: '560px',
        padding: '24px 24px 8px 24px',
        borderRadius: '4px',
        border: `1px solid ${vars.midGray}`,
        background: vars.white,
        boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.30), 0px 4px 8px 3px rgba(0, 0, 0, 0.15)',
    },
};

const titleSx = {
    ...ceraProBold,
    fontSize: '20px',
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: 'normal',
    p: 0,
    pb: 2,
};

const bodySx = {
    ...openSans,
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 'normal',
    pb: 1,
};

const buttonSx = {
    ...ceraProBold,
    textTransform: 'none',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: 'normal',
    color: vars.font,
    padding: '10px 12px',
};

export interface IPasswordDialogProps {
    open: boolean;
    errorMessage: string;
    onConfirm: (password: string) => void;
    onCancel: () => void;
}

export default ({ open, errorMessage, onConfirm, onCancel }: IPasswordDialogProps) => {
    const [password, setPassword] = React.useState('');

    const handleConfirm = () => {
        onConfirm(password);
    };

    const handleCancel = () => {
        setPassword('');
        onCancel();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleConfirm();
        }
    };

    React.useEffect(() => {
        if (!open) {
            setPassword('');
        }
    }, [open]);

    return (
        <Dialog open={open} onClose={handleCancel} sx={dialogSx}>
            <DialogTitle sx={titleSx}>{i18next.t('passwordDialog.title')}</DialogTitle>
            <DialogContent sx={{ px: 0 }}>
                <Typography sx={bodySx}>{i18next.t('passwordDialog.subtext')}</Typography>
                <TextField
                    autoFocus
                    fullWidth
                    type="password"
                    variant="outlined"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={i18next.t('passwordDialog.label')}
                    error={!!errorMessage}
                    helperText={errorMessage}
                />
            </DialogContent>
            <DialogActions sx={{ gap: 1, p: 0 }}>
                <Button
                    variant={'text'}
                    onClick={handleConfirm}
                    disabled={!password}
                    sx={{ ...buttonSx, color: vars.mainGreen }}
                >
                    {i18next.t('passwordDialog.confirmButtonText')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
